/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { faRotate } from '@fortawesome/free-solid-svg-icons/faRotate';
import { Left, Maybe, NonEmptyList, Right } from 'purify-ts';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import { assert, expr, string } from '@codegrade/utils';
import client from '@/client';
import * as comp from '@/components';
import * as models from '@/models';
import { CoursesStore, UserStore } from '@/store';
import * as utils from '@/utils';
import { FontAwesomeIcon } from './Icon';
export default tsx.component({
    name: 'user-transactions',
    props: {
        user: p.ofType().required,
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            transactions: Maybe.empty(),
            usedCoupons: Maybe.empty(),
        };
    },
    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        userId() {
            return this.user.id;
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    created() {
        // We need the courses so we can display links or not.
        CoursesStore.loadPermissions({ force: false });
    },
    watch: {
        userId: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            async handler() {
                this.reloadPage();
            },
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        reloadPage() {
            this.reloadTransactions()
                .ifLeft(err => {
                this.transactions = Maybe.of(Left(err));
            })
                .run();
            this.reloadUsedCoupons()
                .ifLeft(err => {
                this.usedCoupons = Maybe.of(Left(err));
            })
                .run();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        reloadTransactions() {
            const curUserId = this.userId;
            return client.user.getTransactions({ userId: this.user.id }).ifRight(({ cgResult }) => {
                if (curUserId === this.userId) {
                    const transactions = cgResult.map(t => new models.ExtendedTransaction(t));
                    this.transactions = Maybe.of(Right(transactions));
                    if (UserStore.loggedInUser().mapOrDefault(user => user.id === this.userId, false)) {
                        UserStore.putTransactions({ transactions });
                    }
                }
            });
        },
        /** @returns A list of coupons the user has utilized in the past. */
        reloadUsedCoupons() {
            const curUserId = this.userId;
            return client.user.getCouponUsages({ userId: this.user.id }).ifRight(({ cgResult }) => {
                if (curUserId === this.userId) {
                    const usedCoupons = cgResult.map(couponUsage => expr.match(couponUsage, 'scope', {
                        course: c => new api.CourseCouponUsage(c),
                        tenant: c => new api.TenantCouponUsage(c),
                    }));
                    this.usedCoupons = Maybe.of(Right(usedCoupons));
                    if (UserStore.loggedInUser().mapOrDefault(user => user.id === this.userId, false)) {
                        UserStore.putUsedCoupons({ usedCoupons });
                    }
                }
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async clickReloadBtn() {
            const btn = await this.$waitForRef('reload-btn');
            if (btn == null) {
                this.reloadPage();
            }
            else {
                btn.onClick();
            }
        },
        /**
         * Renders the refund button for a specific transaction if it is possible.
         *
         * @param transaction The transaction the refund button will refer to.
         * @returns The refund button if necessary.
         */
        maybeRenderRefundBtn(transaction) {
            if (transaction.state !== 'success') {
                return Maybe.empty();
            }
            const popover = transaction
                .canBeRefunded(transaction.price, this.$getRootInstance().$now)
                .toEither('This transaction can no longer be refunded')
                .swap()
                .toMaybe();
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const askForRefund = () => client.transaction.refund({ transactionId: transaction.id }).map(response => response.addCallbacks({
                onAfterSuccess: () => this.clickReloadBtn(),
            }));
            return Maybe.of(<div v-b-popover_top_hover={popover.orDefault('')} class="my-n2">
                    <comp.SubmitButton disabled={popover.isJust()} label="Request refund" confirm="Are you sure you want to refund this transaction? This means you can no longer use this course, and to pay again you will need to contact our support." submit={askForRefund}/>
                </div>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTransactionState(transaction) {
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const getPopover = () => {
                const state = transaction.state;
                switch (state) {
                    case 'pending':
                        return 'We are waiting for confirmation of the payment.';
                    case 'refunding':
                        return 'We are busy processing your refund.';
                    case 'refunded':
                        return 'We have refunded the transaction.';
                    case 'success':
                        return 'The payment was successful, and you can start with the course.';
                    case 'failed':
                        return 'The payment failed for some reason.';
                    default:
                        return utils.AssertionError.assertNever(state);
                }
            };
            return [
                utils.capitalize(transaction.state),
                <comp.DescriptionPopover hugText={true}>{getPopover()}</comp.DescriptionPopover>,
            ];
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTransaction(transaction) {
            return {
                type: <b-badge variant="primary">Transaction</b-badge>,
                ID: transaction.shortId,
                Course: this.renderCourseName(transaction.price.course),
                'Updated at': (<comp.RelativeTime date={transaction.successAt.orDefault(transaction.updatedAt)} tag="abbr"/>),
                State: this.renderTransactionState(transaction),
                Refund: this.maybeRenderRefundBtn(transaction),
            };
        },
        /**
         * Computes the RenderedSettlement.
         *
         * @param usedCoupon The used coupon to be rendered.
         * @returns The RenderedSettlement object with the content for a Tenant or Course Coupon.
         */
        renderUsedCoupon(usedCoupon) {
            const course = expr.match(usedCoupon, 'scope', {
                course: usage => usage.coupon.coursePrice.course,
                tenant: usage => usage.course,
            });
            return {
                type: (<b-badge variant="primary">
                        {string.capitalize(usedCoupon.scope)} Coupon
                    </b-badge>),
                ID: usedCoupon.id,
                Course: this.renderCourseName(course),
                'Updated at': <comp.RelativeTime date={usedCoupon.createdAt} tag="abbr"/>,
                State: [
                    'Success',
                    <comp.DescriptionPopover hugText={true}>
                        The payment was successful, and you can start with the course.
                    </comp.DescriptionPopover>,
                ],
                Refund: Maybe.empty(),
            };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderCourseName(courseInfo) {
            const route = CoursesStore.getCourse()(courseInfo.id)
                .filter(course => course.canManage)
                .caseOf({
                Just: course => ({
                    name: 'manage_course',
                    params: { courseId: `${course.id}` },
                }),
                Nothing: () => ({
                    name: 'home',
                    query: { filter: courseInfo.name },
                }),
            });
            return (<router-link to={route} class="inline-link">
                    {courseInfo.name}
                </router-link>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderSettlementsTable(renderedSettlements) {
            const showRefund = renderedSettlements.some(x => x.Refund.isJust());
            return (<table class="table table-striped mb-0">
                    <thead>
                        <th class="shrink">Type</th>
                        <th>ID</th>
                        <th>Course</th>
                        <th>Updated at</th>
                        <th class="shrink">State</th>
                        {showRefund && <th class="shrink">Refund</th>}
                    </thead>
                    <tbody>
                        {NonEmptyList.fromArray(renderedSettlements)
                    .map(ss => ss.map(s => (<tr>
                                        <td>{s.type}</td>
                                        <td>{s.ID}</td>
                                        <td>{s.Course}</td>
                                        <td>{s['Updated at']}</td>
                                        <td class="shrink">{s.State}</td>
                                        {showRefund && (<td class="shrink">
                                                {s.Refund.orDefaultLazy(utils.emptyVNode)}
                                            </td>)}
                                    </tr>)))
                    .orDefaultLazy(() => (<tr>
                                    <td colspan={showRefund ? 6 : 5} class="text-muted text-center">
                                        No transactions yet
                                    </td>
                                </tr>))}
                    </tbody>
                </table>);
        },
        /**
         * Render the list of settlements the user was a part of.
         *
         * @param transactions List of transaction made by the user.
         * @param usedCoupons List of coupon used by the user.
         * @returns The rendered list of settlement the user was a part of.
         */
        renderSettlements(transactions, usedCoupons) {
            const settlements = utils.sortBy(transactions.concat(usedCoupons), s => {
                if (s instanceof models.ExtendedTransaction) {
                    return [s.successAt, s.updatedAt];
                }
                else {
                    return [Maybe.of(s.createdAt), s.createdAt];
                }
            }, { reverse: false });
            const renderedSettlements = settlements.map(s => {
                if (s instanceof models.ExtendedTransaction) {
                    return this.renderTransaction(s);
                }
                else if (s instanceof api.CourseCouponUsage ||
                    s instanceof api.TenantCouponUsage) {
                    return this.renderUsedCoupon(s);
                }
                else {
                    return assert.AssertionError.assertNever(s);
                }
            });
            return this.renderSettlementsTable(renderedSettlements);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeRenderUsedCoupons(transactions, data) {
            return data.caseOf({
                Left: err => <comp.CgError error={err} class="m-3"/>,
                Right: usedCoupons => this.renderSettlements(transactions, usedCoupons),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTransactions(transactions) {
            return this.usedCoupons
                .map(data => this.maybeRenderUsedCoupons(transactions, data))
                .orDefaultLazy(() => <comp.Loader scale={1} class="m-3"/>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeRenderTransactions(data) {
            return data.caseOf({
                Left: err => <comp.CgError error={err} class="m-3"/>,
                Right: transactions => this.renderTransactions(transactions),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeRenderReloadBtn() {
            return utils.ifJustOrEmpty(this.transactions, () => (<comp.SubmitButton submit={this.reloadTransactions} ref="reload-btn" class="py-1">
                    <FontAwesomeIcon icon={faRotate}/>
                    <FontAwesomeIcon icon={faRotate} spin slot="pending-label"/>
                </comp.SubmitButton>));
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        return (<b-card noBody class="user-transactions">
                <div class="card-header py-2 align-items-center justify-content-between d-flex">
                    <div class="py-1">Transactions</div>
                    {this.maybeRenderReloadBtn()}
                </div>
                {this.transactions
                .map(data => this.maybeRenderTransactions(data))
                .orDefaultLazy(() => (<comp.Loader scale={1} class="m-3"/>))}
            </b-card>);
    },
});
